import { useState } from "react";

const ENABLER_BLUE = "#3b82f6";
const HERO_URL = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=450&fit=crop";
const AVATAR_URL = "";
const USER_NAME = "Skip Davis";
const HANDLE = "@skipdavis";
const LOCATION = "Arlington, Massachusetts, United States";
const FLAG = "\u{1F1FA}\u{1F1F8}";
const IS_ADMIN = true;

/* ── Shared swoosh arc SVG ── */
function ProfileArc({ size, color = ENABLER_BLUE }) {
  const r = size / 2;
  const stroke = 22;
  const gap = 3;
  const arcR = r + gap + stroke / 2;
  const pad = gap + stroke + 3;
  const svgSize = size + pad * 2;
  const cx = svgSize / 2;
  const cy = svgSize / 2;
  const toRad = (d) => (d * Math.PI) / 180;
  const x1 = cx + arcR * Math.cos(toRad(110));
  const y1 = cy + arcR * Math.sin(toRad(110));
  const x2 = cx + arcR * Math.cos(toRad(85));
  const y2 = cy + arcR * Math.sin(toRad(85));
  const maskConic = `conic-gradient(from 195deg, transparent 0deg, transparent 40deg, black 110deg, black 360deg)`;
  return (
    <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}
      style={{ position: "absolute", top: -pad, left: -pad, pointerEvents: "none",
        filter: `drop-shadow(0 2px 10px rgba(59,130,246,0.35))`,
        WebkitMaskImage: maskConic, maskImage: maskConic }}>
      <path d={`M ${x1} ${y1} A ${arcR} ${arcR} 0 1 1 ${x2} ${y2}`}
        fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="butt" />
      <circle cx={x2} cy={y2} r={stroke / 2} fill={color} />
    </svg>
  );
}

/* ── Avatar bubble ── */
function Avatar({ size, showSwoosh = true, initials = "SD" }) {
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      {showSwoosh && <ProfileArc size={size} />}
      <div style={{
        width: size, height: size, borderRadius: "50%",
        border: "3px solid rgba(255,255,255,0.3)",
        background: AVATAR_URL ? `url(${AVATAR_URL}) center/cover` : "#1e293b",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.35, fontWeight: 700, color: "#94a3b8",
        boxShadow: "0 4px 16px rgba(0,0,0,0.5)", position: "relative",
      }}>
        {!AVATAR_URL && initials}
      </div>
      {/* Online dot */}
      <div style={{
        position: "absolute", bottom: 2, left: 2, width: 20, height: 20,
        borderRadius: "50%", background: "#22c55e",
        border: "2px solid #0f172a", boxShadow: "0 0 4px rgba(34,197,94,0.6)", zIndex: 10,
      }} />
    </div>
  );
}

/* ── Admin badge ── */
function AdminBadge() {
  if (!IS_ADMIN) return null;
  return (
    <span style={{ background: "#7c3aed", color: "#fff", borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      ADMIN
    </span>
  );
}

/* ── Flag badge ── */
function FlagBadge({ size = 48, style = {} }) {
  return <span style={{ fontSize: size, lineHeight: 1, filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))", ...style }}>{FLAG}</span>;
}

/* ── Settings button ── */
function SettingsBtn({ variant = "glass" }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    borderRadius: 8, padding: "7px 16px", fontSize: 13, fontWeight: 600,
    cursor: "pointer", whiteSpace: "nowrap", border: "none",
  };
  const glass = { ...base, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)",
    color: "rgba(255,255,255,0.8)", border: "1px solid rgba(255,255,255,0.15)" };
  const solid = { ...base, background: "#1e293b", color: "#94a3b8", border: "1px solid #334155" };
  const s = variant === "glass" ? glass : solid;
  return (
    <button style={s}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.32 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
      Account Settings
    </button>
  );
}

/* ── Fake tab bar ── */
function TabBar() {
  const tabs = ["Impact", "About", "Knowledge", "Photos", "Feed", "Workspaces"];
  const [active, setActive] = useState(0);
  return (
    <div style={{
      display: "flex", gap: 2, padding: "0 4px",
      borderBottom: "2px solid rgba(255,255,255,0.08)",
      background: "#0f172a",
      borderInlineStart: `4px solid ${ENABLER_BLUE}`,
      borderRadius: "0 0 12px 12px",
    }}>
      {tabs.map((t, i) => (
        <button key={t} onClick={() => setActive(i)} style={{
          background: "none", border: "none", padding: "10px 18px",
          fontSize: 15, fontWeight: i === active ? 700 : 500,
          color: i === active ? ENABLER_BLUE : "rgba(255,255,255,0.45)",
          cursor: "pointer", position: "relative", whiteSpace: "nowrap",
        }}>
          {t}
          {i === active && <div style={{ position: "absolute", bottom: -2, left: 8, right: 8, height: 2, borderRadius: 1, background: ENABLER_BLUE }} />}
        </button>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════ */
/*  OPTION A — Community-style inline                                  */
/* ════════════════════════════════════════════════════════════════════ */

function OptionA() {
  return (
    <div style={{ maxWidth: 1120, margin: "0 auto" }}>
      {/* Hero */}
      <div style={{
        position: "relative", height: 380, borderRadius: "12px 12px 0 0",
        borderInlineStart: `4px solid ${ENABLER_BLUE}`, overflow: "hidden",
        background: `url(${HERO_URL}) center/cover`,
      }}>
        {/* Scrim */}
        <div style={{ position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 45%, transparent 70%)" }} />

        {/* Bottom content */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          padding: "0 28px 20px", display: "flex", alignItems: "flex-end", gap: 18,
        }}>
          <Avatar size={100} />
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <FlagBadge size={28} />
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#fff",
                textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>{USER_NAME}</h1>
              <AdminBadge />
            </div>
            <div style={{ fontSize: 14, color: "rgba(147,197,253,0.95)", fontWeight: 500, marginTop: 2 }}>{HANDLE}</div>
            <div style={{ fontSize: 14, color: "rgba(203,213,225,0.8)", marginTop: 1 }}>{LOCATION}</div>
          </div>
          <SettingsBtn variant="glass" />
        </div>
      </div>
      <TabBar />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════ */
/*  OPTION B — Large avatar with flag tucked on avatar                 */
/* ════════════════════════════════════════════════════════════════════ */

function OptionB() {
  return (
    <div style={{ maxWidth: 1120, margin: "0 auto" }}>
      <div style={{
        position: "relative", height: 380, borderRadius: "12px 12px 0 0",
        borderInlineStart: `4px solid ${ENABLER_BLUE}`, overflow: "hidden",
        background: `url(${HERO_URL}) center/cover`,
      }}>
        <div style={{ position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 45%, transparent 70%)" }} />

        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          padding: "0 28px 20px", display: "flex", alignItems: "flex-end", gap: 20,
        }}>
          {/* Larger avatar with flag tucked at top-right */}
          <div style={{ position: "relative" }}>
            <Avatar size={120} />
            <FlagBadge size={40} style={{ position: "absolute", top: -16, right: -20 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#fff",
                textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>{USER_NAME}</h1>
              <AdminBadge />
            </div>
            <div style={{ fontSize: 14, color: "rgba(147,197,253,0.95)", fontWeight: 500, marginTop: 3 }}>{HANDLE}</div>
            <div style={{ fontSize: 14, color: "rgba(203,213,225,0.8)", marginTop: 1 }}>{LOCATION}</div>
          </div>
          <SettingsBtn variant="glass" />
        </div>
      </div>
      <TabBar />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════ */
/*  OPTION C — Bottom caption bar overlay                              */
/* ════════════════════════════════════════════════════════════════════ */

function OptionC() {
  return (
    <div style={{ maxWidth: 1120, margin: "0 auto" }}>
      <div style={{
        position: "relative", height: 380, borderRadius: "12px 12px 0 0",
        borderInlineStart: `4px solid ${ENABLER_BLUE}`, overflow: "hidden",
        background: `url(${HERO_URL}) center/cover`,
      }}>
        {/* Lighter scrim — bar handles contrast */}
        <div style={{ position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 40%)" }} />

        {/* Caption bar */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          background: "rgba(0,0,0,0.65)", backdropFilter: "blur(12px)",
          padding: "12px 28px", display: "flex", alignItems: "center", gap: 16,
        }}>
          <Avatar size={72} />
          <FlagBadge size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#fff" }}>{USER_NAME}</h1>
              <AdminBadge />
              <span style={{ fontSize: 14, color: "rgba(147,197,253,0.95)", fontWeight: 500 }}>{HANDLE}</span>
            </div>
            <div style={{ fontSize: 13, color: "rgba(203,213,225,0.7)", marginTop: 2 }}>{LOCATION}</div>
          </div>
          <SettingsBtn variant="glass" />
        </div>
      </div>
      <TabBar />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════ */
/*  OPTION D — Avatar overlaps hero/tab boundary (current-ish style)   */
/* ════════════════════════════════════════════════════════════════════ */

function OptionD() {
  return (
    <div style={{ maxWidth: 1120, margin: "0 auto" }}>
      <div style={{
        position: "relative", height: 340, borderRadius: "12px 12px 0 0",
        borderInlineStart: `4px solid ${ENABLER_BLUE}`, overflow: "visible",
        background: `url(${HERO_URL}) center/cover`,
      }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: "12px 12px 0 0",
          background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 50%, transparent 70%)" }} />

        {/* Name + handle overlaid on hero — right of where avatar will be */}
        <div style={{
          position: "absolute", bottom: 16, left: 160, right: 28,
          display: "flex", alignItems: "flex-end", gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#fff",
                textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>{USER_NAME}</h1>
              <AdminBadge />
            </div>
            <div style={{ fontSize: 14, color: "rgba(147,197,253,0.95)", fontWeight: 500, marginTop: 2 }}>{HANDLE}</div>
            <div style={{ fontSize: 14, color: "rgba(203,213,225,0.8)", marginTop: 1 }}>{LOCATION}</div>
          </div>
          <SettingsBtn variant="glass" />
        </div>
      </div>

      {/* Avatar bridges hero and tab bar */}
      <div style={{ position: "relative", zIndex: 5 }}>
        <div style={{ position: "absolute", top: -60, left: 28 }}>
          <div style={{ position: "relative" }}>
            <Avatar size={110} />
            <FlagBadge size={48} style={{ position: "absolute", top: -20, right: -24 }} />
          </div>
        </div>
      </div>
      <TabBar />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════ */
/*  Main explorer                                                       */
/* ════════════════════════════════════════════════════════════════════ */

const OPTIONS = [
  { key: "A", label: "A — Community-style inline", desc: "Flag beside name, avatar + text fully inside hero. Clean and consistent with community pages.", Component: OptionA },
  { key: "B", label: "B — Large avatar, flag on avatar", desc: "Bigger avatar presence, flag tucked at top-right of avatar. More personal/prominent feel.", Component: OptionB },
  { key: "C", label: "C — Frosted caption bar", desc: "Semi-transparent bar at hero bottom. Strongest contrast guarantee, slightly more formal.", Component: OptionC },
  { key: "D", label: "D — Avatar bridges boundary", desc: "Avatar overlaps hero into tab zone (like current). Name stays on hero. Traditional social-profile feel.", Component: OptionD },
];

export default function HeroDesignExplorer() {
  const [selected, setSelected] = useState("A");
  const current = OPTIONS.find((o) => o.key === selected);

  return (
    <div style={{
      background: "#0b1120", color: "#e2e8f0", minHeight: "100vh",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      padding: "24px 32px",
    }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 4px", color: "#fff" }}>
        Profile Hero — Layout Explorer
      </h1>
      <p style={{ fontSize: 14, color: "#94a3b8", margin: "0 0 24px" }}>
        Select an option to preview. All preserve the Enabler blue swoosh, flag badges, online dot, and sticky tab bar.
      </p>

      {/* Option selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
        {OPTIONS.map((o) => (
          <button
            key={o.key}
            onClick={() => setSelected(o.key)}
            style={{
              background: selected === o.key ? ENABLER_BLUE : "#1e293b",
              color: selected === o.key ? "#fff" : "#94a3b8",
              border: selected === o.key ? `2px solid ${ENABLER_BLUE}` : "2px solid #334155",
              borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", transition: "all 0.15s",
            }}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Description */}
      <div style={{
        background: "#1e293b", borderRadius: 8, padding: "12px 16px",
        marginBottom: 20, border: "1px solid #334155",
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#fff", marginBottom: 4 }}>
          Option {current.key}
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.5 }}>
          {current.desc}
        </div>
      </div>

      {/* Preview */}
      <div style={{
        background: "#0f172a", borderRadius: 16, padding: 24,
        border: "1px solid #1e293b",
      }}>
        <current.Component />
      </div>

      {/* Comparison notes */}
      <div style={{
        marginTop: 24, display: "grid", gridTemplateColumns: "1fr 1fr",
        gap: 12,
      }}>
        <div style={{ background: "#1e293b", borderRadius: 8, padding: "12px 16px", border: "1px solid #334155" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: ENABLER_BLUE, marginBottom: 6 }}>Strengths</div>
          <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>
            {selected === "A" && "Cleanest rhyme with community pages. Flag reads as part of the identity line. Nothing breaks the hero boundary."}
            {selected === "B" && "Avatar has stronger presence. Flag on avatar feels personal — 'this is where I'm from'. Swoosh gets more room to breathe."}
            {selected === "C" && "Best contrast regardless of hero photo. Clear separation between photo and identity. Works well on very busy/bright photos."}
            {selected === "D" && "Most traditional social-profile pattern (Facebook, LinkedIn). Avatar bridging creates visual anchor between hero and content."}
          </div>
        </div>
        <div style={{ background: "#1e293b", borderRadius: 8, padding: "12px 16px", border: "1px solid #334155" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#f59e0b", marginBottom: 6 }}>Trade-offs</div>
          <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.6 }}>
            {selected === "A" && "Flag beside name is smaller than on-avatar — less visual weight. Avatar at 100px is slightly smaller than current 117px."}
            {selected === "B" && "Large flag on avatar can look busy with the swoosh. Two decorative elements (flag + arc) competing for attention at same spot."}
            {selected === "C" && "Bar eats into hero photo real estate. Feels slightly more 'app chrome' than the others. Swoosh at 72px loses some of its sweep."}
            {selected === "D" && "Avatar overflow complicates sticky behavior and z-index. Doesn't fully 'rhyme' with community pattern where everything is inside."}
          </div>
        </div>
      </div>
    </div>
  );
}
